/**
 * Asset-aware draw recording + host seam for sandbox tests.
 *
 * `instrumentContext` monkey-patches `drawImage` on a concrete 2D context so
 * every SUCCESSFUL call is recorded (normalized across the 3/5/9-arg forms)
 * against the canvas it paints, keyed in a WeakMap. The host runner (apps/sandbox) installs
 * an `AssetHost` via `setAssetHost` that can resolve a drawn image object back
 * to its AssetManager path — the same injection seam pattern as
 * `setSandboxGameBooter` / `setInputDriver`, so this package keeps ZERO runtime
 * dependency on `@gotti/core`.
 */

/** One recorded ctx.drawImage call, normalized across the 3/5/9-arg forms. */
export interface RecordedDraw {
  /** Asset path resolved via AssetHost.resolveImage; null = not a known asset. */
  assetPath: string | null;
  /** Source subrect (9-arg form only; null for 3/5-arg draws). */
  sx: number | null;
  sy: number | null;
  sw: number | null;
  sh: number | null;
  /** Destination position (all forms). */
  dx: number;
  dy: number;
  /** Destination size (5/9-arg forms; null for the 3-arg draw). */
  dw: number | null;
  dh: number | null;
}

/**
 * Host-injected asset capabilities. Installed by the runner (which owns the
 * real AssetManager) via `setAssetHost`; absent in hosts without asset
 * support, in which case the asset assertions throw loudly instead of
 * silently passing.
 */
export interface AssetHost {
  /** Whether the asset at `path` was preloaded. */
  has(path: string): boolean;
  /** All preloaded asset paths. */
  list(): string[];
  /** Whether game code read the asset (getImage/getJSON/getFile/getUrl). */
  wasAccessed(path: string): boolean;
  /** Resolve an image object (drawImage's first arg) to its asset path. */
  resolveImage(img: unknown): string | null;
  /** Resolve a named frame's pixel rect in a sheet's AnimationAtlas JSON. */
  resolveFrame(sheetPath: string, frame: string): { x: number; y: number; w: number; h: number } | null;
  /** Optional: frame names of the sheet's atlas — used to enrich failure messages. */
  listFrames?(sheetPath: string): string[] | null;
}

let assetHost: AssetHost | null = null;
export function setAssetHost(h: AssetHost | null): void {
  assetHost = h;
}
export function getAssetHost(): AssetHost | null {
  return assetHost;
}

/** Per-canvas draw log. WeakMap so a discarded harness canvas frees its log. */
const drawLog = new WeakMap<HTMLCanvasElement, RecordedDraw[]>();
/** Contexts already instrumented — makes re-instrumenting a no-op. */
const instrumented = new WeakSet<CanvasRenderingContext2D>();

/**
 * Instrument a 2D context so drawImage calls are recorded against its canvas.
 * Direct monkey-patch of `drawImage` on the concrete ctx instance (NOT a
 * Proxy) — everything else is untouched, and the original is forwarded via
 * `Reflect.apply` so behavior/return are byte-identical. Idempotent:
 * re-instrumenting the same ctx is a no-op. Only SUCCESSFUL draws are
 * recorded: the original is forwarded FIRST, so a throwing native drawImage
 * (e.g. a malformed arg count) propagates unchanged and produces NO record.
 * Recording itself can NEVER throw out of drawImage — resolve failures /
 * missing host degrade to `assetPath: null`.
 */
export function instrumentContext(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): CanvasRenderingContext2D {
  if (instrumented.has(ctx)) return ctx;
  instrumented.add(ctx);
  const original = ctx.drawImage;
  ctx.drawImage = function patchedDrawImage(this: CanvasRenderingContext2D, ...args: unknown[]) {
    // Forward FIRST: a draw the canvas rejected must not be logged (it drew
    // nothing), and the native error must reach the caller unchanged.
    const result = Reflect.apply(original as (...a: unknown[]) => unknown, this, args);
    try {
      let assetPath: string | null = null;
      try {
        assetPath = getAssetHost()?.resolveImage(args[0]) ?? null;
      } catch {
        assetPath = null;
      }
      const a = args as number[];
      const rec: RecordedDraw =
        args.length >= 9
          ? { assetPath, sx: a[1], sy: a[2], sw: a[3], sh: a[4], dx: a[5], dy: a[6], dw: a[7], dh: a[8] }
          : args.length >= 5
            ? { assetPath, sx: null, sy: null, sw: null, sh: null, dx: a[1], dy: a[2], dw: a[3], dh: a[4] }
            : { assetPath, sx: null, sy: null, sw: null, sh: null, dx: a[1], dy: a[2], dw: null, dh: null };
      const log = drawLog.get(canvas);
      if (log) log.push(rec);
      else drawLog.set(canvas, [rec]);
    } catch {
      /* recording must never break the game's draw call */
    }
    return result;
  } as typeof ctx.drawImage;
  return ctx;
}

/** Recorded draws for a canvas (insertion order). Unknown/null canvas ⇒ []. */
export function drawsFor(canvas: HTMLCanvasElement | null): RecordedDraw[] {
  if (!canvas) return [];
  return [...(drawLog.get(canvas) ?? [])];
}

/** Drop the recorded draws for a canvas (host calls between tests if needed). */
export function clearDraws(canvas: HTMLCanvasElement): void {
  drawLog.delete(canvas);
}
